import type { ComponentChildren } from "preact";
import { RoomKind, RoomPhase } from "../../../api/types";
import type { RoomInfo } from "../../../api/types";
import { mapName, modeName } from "../../../state/meta";
import { Badge, LockIcon } from "../../components/Badge";
import type { BadgeTone } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Visualizer } from "../../components/Visualizer";
import { cx } from "../../cx";
import { formatClock } from "../../format";
import { isRoomFull, joinBlockReason } from "./roomList";
import type { RoomSort, RoomSortKey, SortDir } from "./roomList";

interface Column {
  key: RoomSortKey;
  label: string;
  cls: string;
  /** Shown instead of the label (which stays for screen readers). */
  icon?: ComponentChildren;
}

const COLUMNS: readonly Column[] = [
  { key: "name", label: "Room", cls: "rooms-table__col--name" },
  { key: "mode", label: "Mode", cls: "rooms-table__col--mode" },
  { key: "map", label: "Map", cls: "rooms-table__col--map" },
  { key: "players", label: "Players", cls: "rooms-table__col--players" },
  { key: "phase", label: "Phase", cls: "rooms-table__col--phase" },
  { key: "time", label: "Time", cls: "rooms-table__col--time" },
  { key: "lock", label: "Password", cls: "rooms-table__col--lock", icon: <LockIcon size={13} /> },
  { key: "region", label: "Region", cls: "rooms-table__col--region" },
];

/** Sortable columns plus the Join column. */
const COLUMN_COUNT = COLUMNS.length + 1;

const PHASES: Record<RoomPhase, { label: string; tone: BadgeTone }> = {
  [RoomPhase.WARMUP]: { label: "Warmup", tone: "teal" },
  [RoomPhase.LIVE]: { label: "Live", tone: "ok" },
  [RoomPhase.ENDED]: { label: "Ended", tone: "neutral" },
};

const SKELETON_ROWS = Array.from({ length: 8 }, (_, i) => i);
const SKELETON_CELLS = Array.from({ length: COLUMN_COUNT }, (_, i) => i);

function SortArrow({ dir }: { dir: SortDir | null }) {
  return (
    <span class="dtable__sort-arrow rooms-table__arrow" aria-hidden="true">
      {dir && (
        <svg class={cx(dir === "desc" && "is-desc")} viewBox="0 0 10 10" width="10" height="10" focusable="false">
          <path d="M5 2 9 8H1z" fill="currentColor" />
        </svg>
      )}
    </span>
  );
}

interface RoomRowProps {
  room: RoomInfo;
  /** This row's join request is in flight. */
  joining: boolean;
  /** Another row is joining; every Join button waits. */
  locked: boolean;
  onJoin: (room: RoomInfo) => void;
}

function RoomRow({ room, joining, locked, onJoin }: RoomRowProps) {
  const reason = joinBlockReason(room);
  const phase = PHASES[room.phase];
  const fill = room.maxPlayers > 0 ? Math.min(100, (room.players / room.maxPlayers) * 100) : 0;
  return (
    <tr
      class={cx("rooms-table__row", reason !== null && "is-unavailable")}
      tabIndex={0}
      data-nav=""
      onKeyDown={(e) => {
        // Enter on the Join button is that button's own click.
        if (e.key !== "Enter" || e.target !== e.currentTarget) return;
        e.preventDefault();
        onJoin(room);
      }}
    >
      <td>
        <div class="rooms-table__name">
          <span class="rooms-table__line">
            <span class="rooms-table__title" title={room.name}>
              {room.name}
            </span>
            {room.kind === RoomKind.CUSTOM && <Badge>Custom</Badge>}
          </span>
          {room.host && <span class="rooms-table__host">Host: {room.host}</span>}
        </div>
      </td>
      <td>{modeName(room.mode)}</td>
      <td>{mapName(room.map)}</td>
      <td>
        <div class="rooms-table__players">
          <span class="rooms-table__count">
            <span class="lcd rooms-table__now" data-ghost="88">
              {room.players}
            </span>
            <span class="rooms-table__max">/{room.maxPlayers}</span>
            {room.bots > 0 && <span class="rooms-table__bots" title="Bots leave as players join">+{room.bots} bots</span>}
          </span>
          <span class="rooms-table__fill" aria-hidden="true">
            <span class={cx("rooms-table__fill-bar", isRoomFull(room) && "is-full")} style={{ width: `${fill}%` }} />
          </span>
        </div>
      </td>
      <td>
        <Badge tone={phase.tone}>{phase.label}</Badge>
      </td>
      <td>
        <div class="rooms-table__time num">
          {room.timeLeftSec === undefined ? (
            <span class="rooms-table__none">{"\u2014"}</span>
          ) : (
            <span class="lcd rooms-table__clock" data-ghost="88:88">
              {formatClock(room.timeLeftSec)}
            </span>
          )}
          {room.scores && (
            <span class="rooms-table__score" title="Score">
              <span class="rooms-table__score-a">{room.scores.a}</span> {"\u2013"}{" "}
              <span class="rooms-table__score-b">{room.scores.b}</span>
            </span>
          )}
        </div>
      </td>
      <td class="rooms-table__lock">
        {room.hasPassword && (
          <span role="img" aria-label="Password protected" title="Password protected">
            <LockIcon size={15} />
          </span>
        )}
      </td>
      <td class="rooms-table__region">{room.region}</td>
      <td class="rooms-table__join">
        <Button
          size="sm"
          variant="primary"
          busy={joining}
          disabled={reason !== null || locked}
          title={reason ?? undefined}
          aria-label={`Join ${room.name}`}
          onClick={() => onJoin(room)}
        >
          Join
        </Button>
      </td>
    </tr>
  );
}

export type RoomTableState = "loading" | "failed" | "ready";

export interface RoomTableProps {
  state: RoomTableState;
  /** Rooms to show: filtered and sorted. */
  rooms: readonly RoomInfo[];
  /** Rooms the server listed, before filtering. */
  listedCount: number;
  filtersActive: boolean;
  onClearFilters: () => void;
  sort: RoomSort;
  onSort: (key: RoomSortKey) => void;
  joiningId: string | null;
  onJoin: (room: RoomInfo) => void;
}

function StateRow({
  title,
  hint,
  action,
  failed = false,
}: {
  title: string;
  hint: string;
  action?: ComponentChildren;
  failed?: boolean;
}) {
  return (
    <tr class={cx("rooms-table__state", failed && "rooms-table__state--failed")}>
      <td colSpan={COLUMN_COUNT}>
        <div class="rooms-table__message">
          <Visualizer class="rooms-table__viz" bars={9} />
          <strong class="rooms-table__message-title">{title}</strong>
          <span class="rooms-table__message-hint">{hint}</span>
          {action}
        </div>
      </td>
    </tr>
  );
}

/** Sortable room table. The column widths are fixed, so data changes never move the columns. */
export function RoomTable({
  state,
  rooms,
  listedCount,
  filtersActive,
  onClearFilters,
  sort,
  onSort,
  joiningId,
  onJoin,
}: RoomTableProps) {
  return (
    <table class="dtable rooms-table" aria-label="Rooms" aria-busy={state === "loading"}>
      <colgroup>
        {COLUMNS.map((col) => (
          <col key={col.key} class={col.cls} />
        ))}
        <col class="rooms-table__col--join" />
      </colgroup>
      <thead>
        <tr>
          {COLUMNS.map((col) => {
            const dir = sort.key === col.key ? sort.dir : null;
            return (
              <th
                key={col.key}
                scope="col"
                aria-sort={dir === null ? "none" : dir === "asc" ? "ascending" : "descending"}
              >
                <button type="button" class="dtable__sort" onClick={() => onSort(col.key)} data-nav="">
                  {col.icon}
                  <span class={col.icon ? "sr-only" : undefined}>{col.label}</span>
                  <SortArrow dir={dir} />
                </button>
              </th>
            );
          })}
          <th scope="col">
            <span class="sr-only">Join</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {state === "loading" &&
          SKELETON_ROWS.map((row) => (
            <tr key={row} class="rooms-table__skeleton" aria-hidden="true">
              {SKELETON_CELLS.map((cell) => (
                <td key={cell}>
                  <span class="rooms-table__skel" />
                </td>
              ))}
            </tr>
          ))}
        {state === "failed" && (
          <StateRow
            failed
            title="Could not load the room list"
            hint="The list reloads automatically. You can also press Refresh."
          />
        )}
        {state === "ready" && rooms.length === 0 && listedCount === 0 && (
          <StateRow title="No rooms are listed right now" hint="Create a lobby to start one." />
        )}
        {state === "ready" && rooms.length === 0 && listedCount > 0 && (
          <StateRow
            title="No rooms match your filters"
            hint="Try another mode or map, or loosen the filters."
            action={
              filtersActive ? (
                <Button size="sm" onClick={onClearFilters}>
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        )}
        {state === "ready" &&
          rooms.map((room) => (
            <RoomRow
              key={room.id}
              room={room}
              joining={joiningId === room.id}
              locked={joiningId !== null && joiningId !== room.id}
              onJoin={onJoin}
            />
          ))}
      </tbody>
    </table>
  );
}
